import AlertMenu from "../common/AlertMenu";
import Avatar from "../common/Avatar";
import AppContext from "../../state/AppContext";
import UserContext from "../../state/UserContext";
import { useContext, useEffect, useState } from "react";
import { DropDownMenu } from "../common";
import { DropDownMenuItem } from "../common/DropDownMenu";
import NonPrimaryNavItems from "../../routes/NonPrimaryNavItems";
import AppRoutes from "../../routes/AppRoutes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/pro-regular-svg-icons";
import ProfilePictureModal from "./ProfilePictureModal";
import { localStorageHelper, localStorageKeys } from "../../helpers";
import { isAccessibeEnabledForClient } from "../../config/third-party/accessibeConfig";

function ProfileNav() {
  const appContext = useContext(AppContext);
  const userContext = useContext(UserContext);
  const [hasLegacyReviews, setHasLegacyReviews] = useState<boolean>(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] =
    useState(false);

  const reviewHistoryObject = {
    text: "Nav.Profile.ReviewHistory",
    href: AppRoutes.reviewHistory.path,
  };

  const navItemsToShow: DropDownMenuItem[] = [...NonPrimaryNavItems];

  useEffect(() => {
    setHasLegacyReviews(userContext.user.legacyDetails.selfReviews);
  }, [userContext.user.legacyDetails]);

  // Conditionally add the historical reviews link if the user has legacy reviews
  if (
    hasLegacyReviews &&
    navItemsToShow.find((navItem) => navItem.text.includes("ReviewHistory")) ===
      undefined
  ) {
    // the logout option should always be last
    const reviewHistroyPosition = navItemsToShow.length - 1;
    navItemsToShow.splice(reviewHistroyPosition, 0, reviewHistoryObject);
  }

  // Only offer the toggle where we would actually load Accessibe, so it can never
  // appear as a control that does nothing
  if (isAccessibeEnabledForClient(userContext.user.client.id)) {
    const isHidden = appContext.accessibilityToolHidden;

    const toggleAccessibilityTool = () => {
      const employeeId = userContext.user.id;
      const storageKey = localStorageKeys.accessibilityToolHidden(employeeId);

      if (isHidden) {
        localStorageHelper.removeItem(storageKey);
      } else {
        localStorageHelper.setItem(storageKey, "true");
      }

      appContext.setAccessibilityToolHidden(!isHidden);
    };

    // The logout option should always be last
    navItemsToShow.splice(navItemsToShow.length - 1, 0, {
      text: isHidden
        ? "Nav.Profile.ShowAccessibilityTool"
        : "Nav.Profile.HideAccessibilityTool",
      defaultText: isHidden
        ? "Show Accessibility Tool"
        : "Hide Accessibility Tool",
      onSelect: toggleAccessibilityTool,
    });
  }

  // Conditionally add the demo reset link if the user is a demo user
  if (
    userContext.user.isManager &&
    userContext.user.client.enabledModules.indexOf("Demo Features") > -1
  ) {
    navItemsToShow.unshift({
      text: "Reset Demo",
      href: AppRoutes.demo.managerReset,
    });
  }

  const handleEditProfilePicture = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsProfilePictureModalOpen(true);
  };

  return (
    <div className="flex flex-row items-center">
      <AlertMenu
        defaultAlertData={userContext.user.notifications}
      />
      <div className="group relative mr-1">
        <Avatar
          userFullName={userContext.user.firstName}
          userInitials={userContext.user.initials}
          size={10}
          imageUrl={userContext.user.profilePic}
        />
        <button
          type="button"
          onClick={handleEditProfilePicture}
          aria-label="Edit profile picture"
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity focus:opacity-100 focus:outline-none"
        >
          <FontAwesomeIcon
            icon={faPen}
            className="text-white"
            size="sm"
          />
        </button>
      </div>
      <DropDownMenu eventType="NAVIGATE" items={navItemsToShow} />
      <ProfilePictureModal
        isOpen={isProfilePictureModalOpen}
        onOpenChange={setIsProfilePictureModalOpen}
      />
    </div>
  );
}

export default ProfileNav;
